import { useState } from 'react';
import { useAtom, type PrimitiveAtom } from 'jotai';
import type { TableState } from 'components/data-table';

interface PagedFilters {
  page: number;
  perPage: number;
}

/**
 * A server-paged admin log's filter state, held in its atom: the filters, a
 * patch that returns to the first page, the pager's handler, and the filter
 * bar's Reset. Reset also bumps a key the bar puts on its stateful inputs, so
 * the remount drops a search still waiting on its debounce and a half-picked
 * date range.
 * @param {PrimitiveAtom<F>} filtersAtom - The log's filter, sort and page state
 * @param {F} empty - The state Reset returns to
 * @return The filters and their handlers
 */
export const useLogFilters = <F extends PagedFilters>(
  filtersAtom: PrimitiveAtom<F>,
  empty: F
) => {
  const [filters, setFilters] = useAtom(filtersAtom);
  const [resetKey, setResetKey] = useState(0);

  /**
   * Applies filter changes and returns to the first page.
   * @param {Partial<F>} changes - The filters to change
   */
  const patchFilter = (changes: Partial<F>) =>
    setFilters((prev) => ({ ...prev, page: 1, ...changes }));

  /**
   * Writes the pager's page and page size.
   * @param {Pick<TableState, 'page' | 'pageSize'>} state - The table's pagination
   */
  const onPaginationChange = ({
    page,
    pageSize,
  }: Pick<TableState, 'page' | 'pageSize'>) =>
    setFilters((prev) => ({ ...prev, page, perPage: pageSize }));

  /**
   * Resets the filters and remounts the bar's stateful inputs.
   */
  const reset = () => {
    setFilters(empty);
    setResetKey((key) => key + 1);
  };

  return { filters, patchFilter, onPaginationChange, resetKey, reset };
};
